// Sidebar model. Built from GET /api/v1/_schema entity.nav (labels, groups, and the order of entries that are
// not built in), with Drata's sidebar as the static fallback and the fixed order of the built-in entries;
// modules can add or replace entries; entries the viewer cannot read are hidden.
import type { NavEntry } from './modules'
import { canReadModule, entityAccess, isEmployeeOnly, isStaff, type Viewer } from './permissions'
import type { NavGroup, SchemaIndex } from './schema'

export interface NavSection {
  group: NavGroup
  /** Uppercase group header, or null for the unlabeled top and bottom sections. */
  label: string | null
  items: NavEntry[]
}

export const NAV_GROUPS: Array<{ group: NavGroup; label: string | null }> = [
  { group: 'Home', label: null },
  { group: 'Compliance', label: 'Compliance' },
  { group: 'Risk', label: 'Risk' },
  { group: 'Governance', label: 'Governance' },
  { group: 'Trust', label: 'Trust' },
  { group: 'Settings', label: null },
]

/** Drata's sidebar (Clearpol tenant, 2026-10-01), mapped to our entity routes. Used when the schema has no nav. */
export const STATIC_NAV: NavEntry[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    to: '/',
    group: 'Home',
    order: 10,
    icon: 'dashboard',
    visible: isStaff,
  },
  {
    key: 'tasks',
    label: 'Tasks',
    to: '/tasks',
    group: 'Home',
    order: 20,
    icon: 'tasks',
    entity: 'task',
    module: 'tasks',
  },
  {
    key: 'controls',
    label: 'Controls',
    to: '/controls',
    group: 'Compliance',
    order: 10,
    icon: 'controls',
    entity: 'control',
    module: 'controls',
  },
  {
    key: 'frameworks',
    label: 'Frameworks',
    to: '/frameworks',
    group: 'Compliance',
    order: 20,
    icon: 'frameworks',
    entity: 'framework',
    module: 'frameworks',
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    to: '/monitors',
    group: 'Compliance',
    order: 30,
    icon: 'monitoring',
    entity: 'monitor',
    module: 'monitoring',
  },
  {
    key: 'events',
    label: 'Event Tracking',
    to: '/events',
    group: 'Compliance',
    order: 35,
    icon: 'events',
    entity: 'event',
    module: 'events',
  },
  {
    key: 'evidence',
    label: 'Evidence Library',
    to: '/evidence',
    group: 'Compliance',
    order: 40,
    icon: 'evidence',
    entity: 'evidence_item',
    module: 'evidence',
  },
  {
    key: 'audits',
    label: 'Audit Hub',
    to: '/audits',
    group: 'Compliance',
    order: 50,
    icon: 'audits',
    entity: 'audit',
    module: 'audits',
  },
  {
    key: 'risks',
    label: 'Risk Assessment',
    to: '/risks',
    group: 'Risk',
    order: 10,
    icon: 'risks',
    entity: 'risk',
    module: 'risks',
  },
  {
    key: 'vendors',
    label: 'Vendors',
    to: '/vendors',
    group: 'Risk',
    order: 20,
    icon: 'vendors',
    entity: 'vendor',
    module: 'vendors',
  },
  {
    key: 'assets',
    label: 'Assets',
    to: '/assets',
    group: 'Risk',
    order: 30,
    icon: 'assets',
    entity: 'asset',
    module: 'assets',
  },
  {
    key: 'vulnerabilities',
    label: 'Vulnerabilities',
    to: '/vulnerabilities',
    group: 'Risk',
    order: 40,
    icon: 'vulnerabilities',
    entity: 'vulnerability',
    module: 'vulnerabilities',
  },
  {
    key: 'personnel',
    label: 'Personnel',
    to: '/personnel',
    group: 'Governance',
    order: 10,
    icon: 'personnel',
    entity: 'personnel',
    module: 'personnel',
  },
  {
    key: 'policies',
    label: 'Policies',
    to: '/policies',
    group: 'Governance',
    order: 20,
    icon: 'policies',
    entity: 'policy',
    module: 'policies',
  },
  {
    key: 'access-reviews',
    label: 'Access Review',
    to: '/access-reviews',
    group: 'Governance',
    order: 30,
    icon: 'access-reviews',
    entity: 'access_review',
    module: 'access-reviews',
  },
  {
    key: 'trust',
    label: 'Trust Center',
    to: '/trust-sections',
    group: 'Trust',
    order: 10,
    icon: 'trust',
    entity: 'trust_section',
    module: 'trust',
  },
  {
    key: 'connections',
    label: 'Connections',
    to: '/connections',
    group: 'Settings',
    order: 5,
    icon: 'connections',
    entity: 'connection',
    module: 'connections',
  },
  {
    key: 'settings',
    label: 'Settings',
    to: '/settings',
    group: 'Settings',
    order: 100,
    icon: 'settings',
    visible: isStaff,
  },
]

/** The employee portal. The only entry for people with just the EMPLOYEE role. */
export const MY_COMPLIANCE: NavEntry = {
  key: 'me',
  label: 'My Compliance',
  to: '/me',
  group: 'Home',
  order: 5,
  icon: 'me',
}

/** Entities with a schema nav entry that live inside another page instead of their own sidebar item. */
export const ABSORBED_NAV: Record<string, string> = { user: 'settings' }

function visible(entry: NavEntry, viewer: Viewer, schema: SchemaIndex | null): boolean {
  if (entry.visible) return entry.visible(viewer)
  if (entry.entity) {
    const e = schema?.entity(entry.entity)
    if (e) return entityAccess(viewer, e).read
    if (schema) return false
    // Schema not available yet: fall back to the module's read rule.
    return entry.module ? canReadModule(viewer, entry.module) : false
  }
  if (entry.module) return canReadModule(viewer, entry.module)
  return true
}

export function buildNav(opts: {
  schema: SchemaIndex | null
  viewer: Viewer
  extra?: NavEntry[]
}): NavSection[] {
  const { schema, viewer } = opts
  const byKey = new Map<string, NavEntry>()
  for (const e of STATIC_NAV) byKey.set(e.key, { ...e })

  if (schema) {
    for (const ent of schema.entities) {
      if (!ent.nav || ABSORBED_NAV[ent.name]) continue
      const existing = [...byKey.values()].find((n) => n.entity === ent.name)
      const label = ent.nav.label ?? ent.labelPlural
      if (existing) {
        // Drata parity: a built-in entry keeps its Drata position inside its group (e.g. Event Tracking sits
        // between Monitoring and Evidence Library). The schema still supplies the label, and moving the
        // entity to another group moves the entry, using the schema order there.
        const sameGroup = existing.group === ent.nav.group
        byKey.set(existing.key, {
          ...existing,
          label,
          group: ent.nav.group,
          order: sameGroup ? existing.order : ent.nav.order,
        })
      } else {
        byKey.set(ent.name, {
          key: ent.name,
          label,
          to: `/${ent.plural}`,
          group: ent.nav.group,
          order: ent.nav.order,
          entity: ent.name,
          module: ent.module,
        })
      }
    }
  }

  let me: NavEntry = MY_COMPLIANCE
  for (const n of opts.extra ?? []) {
    if (n.key === 'me') me = n
    else byKey.set(n.key, n)
  }

  if (isEmployeeOnly(viewer)) {
    return [{ group: 'Home', label: null, items: [me] }]
  }

  const sections: NavSection[] = []
  for (const g of NAV_GROUPS) {
    const items = [...byKey.values()]
      .filter((n) => n.group === g.group && visible(n, viewer, schema))
      .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
    if (items.length) sections.push({ group: g.group, label: g.label, items })
  }
  return sections
}

/** True when the path is the entry's page or one of its detail pages. */
export function isNavActive(entry: Pick<NavEntry, 'to'>, pathname: string): boolean {
  if (entry.to === '/') return pathname === '/'
  return pathname === entry.to || pathname.startsWith(`${entry.to}/`)
}
